import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { Download, Copy, Check, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
};

export const BulkAccommodationModal = ({ isOpen, onClose, tableId }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [copiedRoom, setCopiedRoom] = useState<string | null>(null);
  const { tables } = useAccommodationStore();
  const navigate = useNavigate();
  const { translations } = useLanguage();

  // Get available rooms for the current table
  const availableRooms = tables && tables[tableId] && tables[tableId].r ? tables[tableId].r.map(room => ({
    number: room.n,
    type: room.t === 'd' ? 'double' : room.t === 't' ? 'triple' : 'quadruple',
    capacity: room.c,
    occupied: room.o
  })) : [];

  const handleCopyRoomNumber = async (roomNumber: string) => {
    try {
      await navigator.clipboard.writeText(roomNumber);
      setCopiedRoom(roomNumber);
      setTimeout(() => setCopiedRoom(null), 2000);
      toast.success('Room number copied');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        'Room Number': '101',
        'Bed Index': '1',
        'Passport Number': '123456789'
      },
      {
        'Room Number': '101',
        'Bed Index': '2',
        'Passport Number': '987654321'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-accommodation-template.xlsx');
  };

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-accommodation-status', {
          state: { 
            fileData: data,
            tableId 
          }
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.bulkAccommodation?.title || "Bulk Accommodation Assignment"}>
      <div className="space-y-6">
        {/* Available Rooms */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium mb-3">Available Rooms</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {availableRooms.map((room) => (
              <motion.div
                key={room.number}
                whileHover={{ scale: 1.02 }}
                className="bg-white p-3 rounded-lg shadow-sm relative group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{room.number}</div>
                    <div className="text-xs text-gray-500">{room.type}</div>
                    <div className="text-xs text-gray-500">
                      {room.occupied}/{room.capacity}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopyRoomNumber(room.number.toString())}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    {copiedRoom === room.number.toString() ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.bulkAccommodation?.downloadTemplate || "Download Excel Template"}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => setFile(null)}
        />

        <ValidationErrors errors={[]} />
        <WarningMessage />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.bulkAccommodation?.cancel || "Cancel"}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className={`px-6 py-2 rounded-lg text-white
              ${!file ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.bulkAccommodation?.upload || "Upload"}
          </button>
        </div>
      </div>
    </Modal>
  );
};